// Shared JSON-LD builders for "Basel mit Kindern".
// All builders return plain JS objects (or null) ready for JSON.stringify.
// Values are pulled live from entity records so every crawl sees current data.

import { activityPath } from '@/lib/activityUrl';

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_NAMES = {
  monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday',
  thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday',
};

export function siteOrigin() {
  if (typeof window === 'undefined') return '';
  return window.location.origin;
}

export function activityUrl(activity, categories = []) {
  const path = activityPath(activity, categories);
  return path ? `${siteOrigin()}${path}` : '';
}

function stripHtml(html) {
  if (!html) return '';
  return String(html).replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

// Group days that share identical opening hours into one OpeningHoursSpecification.
export function buildOpeningHoursSpec(activity) {
  if (activity.always_open) {
    return [{
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: DAY_KEYS.map(d => DAY_NAMES[d]),
      opens: '00:00',
      closes: '23:59',
    }];
  }
  const hours = activity.opening_hours;
  if (!hours) return [];
  const groups = {};
  DAY_KEYS.forEach((key) => {
    const val = (hours[key] || '').trim();
    if (!val) return;
    (groups[val] = groups[val] || []).push(DAY_NAMES[key]);
  });
  const specs = [];
  Object.entries(groups).forEach(([val, days]) => {
    const parts = val.split('-').map(s => s.trim());
    if (parts.length !== 2 || !parts[0] || !parts[1]) return; // skip "nach Vereinbarung" etc.
    specs.push({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: days.length === 1 ? days[0] : days,
      opens: parts[0],
      closes: parts[1],
    });
  });
  return specs;
}

function priceRangeLabel(pr, t) {
  const map = {
    gratis: t('Kostenlos', 'Free'),
    guenstig: 'CHF 1-10',
    mittel: 'CHF 10-25',
    teuer: 'CHF 25+',
  };
  return map[pr] || pr;
}

function buildOffers(activity, t) {
  const entries = activity.eintritt;
  if (!Array.isArray(entries) || entries.length === 0) return null;
  const first = entries[0];
  const priceStr = String(first.price || '0');
  const priceNum = parseFloat(priceStr.replace(/[^0-9.]/g, ''));
  return {
    '@type': 'Offer',
    name: first.label || t('Eintritt', 'Admission'),
    price: isNaN(priceNum) ? 0 : priceNum,
    priceCurrency: 'CHF',
  };
}

function buildAmenities(activity, t) {
  const out = [];
  const add = (name, value) => {
    if (value === undefined || value === null || value === '') return;
    out.push({ '@type': 'LocationFeatureSpecification', name, value });
  };
  const acc = activity.accessibility || [];
  if (acc.includes('kinderwagen')) add(t('Kinderwagen-freundlich', 'Stroller-friendly'), true);
  if (acc.includes('rollstuhl')) add(t('Rollstuhlgerecht', 'Wheelchair accessible'), true);
  add(t('Hunde erlaubt', 'Dogs allowed'), !!activity.dogs_allowed);
  if (activity.parking_available) {
    add(t('Parkplatz', 'Parking'),
      activity.parking_type === 'kostenlos' ? t('Kostenlos', 'Free') : t('Gebührenpflichtig', 'Paid'));
  }
  return out;
}

function buildAdditionalProperties(activity, { categories, seasons, getField, t }) {
  const out = [];
  const add = (name, value) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value) && value.length === 0) return;
    out.push({ '@type': 'PropertyValue', name, value: Array.isArray(value) ? value.join(', ') : value });
  };

  const actCats = (activity.category_ids || [])
    .map(id => categories.find(c => c.id === id))
    .filter(Boolean);
  const parents = actCats.filter(c => !c.parent_category_id);
  const subs = actCats.filter(c => c.parent_category_id);
  if (parents.length) add(t('Kategorie', 'Category'), parents.map(c => getField(c, 'name')));
  if (subs.length) add(t('Unterkategorie', 'Subcategory'), subs.map(c => getField(c, 'name')));

  const actSeasons = (activity.season_ids || [])
    .map(id => seasons.find(s => s.id === id))
    .filter(Boolean);
  if (actSeasons.length) add(t('Jahreszeit', 'Season'), actSeasons.map(s => getField(s, 'name')));

  if (activity.best_time_of_day?.length) add(t('Beste Tageszeit', 'Best time of day'), activity.best_time_of_day);
  if (activity.intensity) add(t('Intensität', 'Intensity'), activity.intensity);
  if (activity.duration) add(t('Dauer', 'Duration'), activity.duration);
  if (activity.activity_class) add(t('Klassifizierung', 'Classification'), activity.activity_class);
  if (typeof activity.popularity_score === 'number') add('Popularity Score', activity.popularity_score);

  return out;
}

// Build a per-activity JSON-LD object. Multi-types with LocalBusiness so that
// priceRange / aggregateRating are valid and review-star rich results can fire.
export function buildActivityJsonLd(activity, opts = {}) {
  if (!activity) return null;
  const {
    categories = [], reviews = [], seasons = [], getField = (a, f) => a[f], t = (de) => de,
  } = opts;

  const declared = activity.structured_data_type || 'TouristAttraction';
  const types = declared === 'LocalBusiness'
    ? ['LocalBusiness']
    : [declared, 'LocalBusiness'];

  const data = {
    '@context': 'https://schema.org',
    '@type': types.length === 1 ? types[0] : types,
    name: getField(activity, 'title'),
    url: activityUrl(activity, categories),
  };

  const desc = getField(activity, 'teaser_description') || getField(activity, 'meta_description');
  if (desc) data.description = desc;

  const img = activity.og_image || activity.image;
  if (img) data.image = img;

  if (activity.website_url) data.sameAs = activity.website_url;

  const addr = activity.address;
  if (addr && (addr.street || addr.city || addr.postal_code)) {
    data.address = {
      '@type': 'PostalAddress',
      addressCountry: 'CH',
      ...(addr.street ? { streetAddress: addr.street } : {}),
      ...(addr.city ? { addressLocality: addr.city } : {}),
      ...(addr.postal_code ? { postalCode: addr.postal_code } : {}),
    };
  }

  if (addr && typeof addr.latitude === 'number' && typeof addr.longitude === 'number') {
    data.geo = { '@type': 'GeoCoordinates', latitude: addr.latitude, longitude: addr.longitude };
  }

  if (typeof activity.age_min === 'number' || typeof activity.age_max === 'number') {
    data.audience = {
      '@type': 'PeopleAudience',
      suggestedMinAge: activity.age_min ?? 0,
      suggestedMaxAge: activity.age_max ?? 16,
    };
  }

  data.isAccessibleForFree = activity.price_range === 'gratis';
  if (activity.price_range) data.priceRange = priceRangeLabel(activity.price_range, t);

  const offers = buildOffers(activity, t);
  if (offers) data.offers = offers;

  const oh = buildOpeningHoursSpec(activity);
  if (oh.length) data.openingHoursSpecification = oh;

  const amenities = buildAmenities(activity, t);
  if (amenities.length) data.amenityFeature = amenities;

  const props = buildAdditionalProperties(activity, { categories, seasons, getField, t });
  if (props.length) data.additionalProperty = props;

  return data;
}

// CollectionPage with an ItemList of activities. Used on the canonical
// (unfiltered) Ausflugsideen view and on category/landingpages.
export function buildCollectionPageJsonLd({ name, description, url, activities, categories = [], language = 'de' }) {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name,
    url,
    inLanguage: language === 'en' ? 'en-CH' : 'de-CH',
  };
  if (description) data.description = stripHtml(description);

  data.publisher = { '@type': 'Organization', name: 'Basel mit Kindern', url: siteOrigin() };
  data.about = { '@type': 'City', name: 'Basel', addressCountry: 'CH' };
  data.audience = {
    '@type': 'PeopleAudience',
    audienceType: language === 'en' ? 'Families with children' : 'Familien mit Kindern',
    suggestedMinAge: 0,
    suggestedMaxAge: 16,
  };

  if (activities && activities.length) {
    data.mainEntity = {
      '@type': 'ItemList',
      numberOfItems: activities.length,
      itemListOrder: 'https://schema.org/ItemListUnordered',
      itemListElement: activities.map((a, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: activityUrl(a, categories),
        name: a.title_de || a.title_en,
      })),
    };
  }

  return data;
}